import {EnrichedAstNode, AstPosition} from '../types';
import {
    getDistanceFromBounds,
    getXBounds,
    getYBounds,
    Gap,
    getXBoundsWithMargin,
    getYBoundsWithMargin,
} from '../helpers/GeometryHelpers';

export type Gaps = {
    x: Gap[];
    y: Gap[];
};

// Write new checkGaps based on childNode geo. Then use parent geo to get padding. If padding is negative. Adjust child. Check for edge case of not overlapped child.
export function checkGaps(position: AstPosition, flexChildren: EnrichedAstNode[]): Gaps {
    // This is the scheduler problem
    // Thank alex for teaching us

    const result = {
        x: [getXBounds(position)],
        y: [getYBounds(position)],
    };

    flexChildren
        // .filter((child) => child.styles.position !== 'absolute') // Don't worry about abs children.
        .forEach((node) => {
            // remove child dims from result gaps.
            // x
            const xBounds = getXBoundsWithMargin(node.position);
            result.x = result.x.reduce(
                gapReducer(xBounds),
                [] as {
                    start: number;
                    end: number;
                }[]
            );

            // y
            const yBounds = getYBoundsWithMargin(node.position);
            result.y = result.y.reduce(
                gapReducer(yBounds),
                [] as {
                    start: number;
                    end: number;
                }[]
            );
        });

    // remove duplicates gaps... probably a more correct algo but this works?

    result.x = removeDuplicateGapValues(result.x);
    result.y = removeDuplicateGapValues(result.y);

    // if missing start gap add. or don't
    result.x = addBackEdgeGaps(position.absoluteX, position.absoluteX + position.width, result.x);
    result.y = addBackEdgeGaps(position.absoluteY, position.absoluteY + position.height, result.y);

    return result;
}

function getDistanceToParentInnerPosition(parent: EnrichedAstNode, child: EnrichedAstNode) {
    const parentSides = getSidesOfNode(parent.innerPosition);
    const childSides = getSidesOfNode(child.position);

    return {
        left: childSides.left - parentSides.left,
        right: parentSides.right - childSides.right,
        top: childSides.top - parentSides.top,
        bottom: parentSides.bottom - childSides.bottom,
    };
}

function getDistanceToParentPosition(parent: EnrichedAstNode, child: EnrichedAstNode) {
    const parentSides = getSidesOfNode(parent.position);
    const childSides = getSidesOfNode(child.position);

    return {
        left: childSides.left - parentSides.left,
        right: parentSides.right - childSides.right,
        top: childSides.top - parentSides.top,
        bottom: parentSides.bottom - childSides.bottom,
    };
}

export function getDistanceToParentFlexLabels(
    parent: EnrichedAstNode,
    child: EnrichedAstNode,
    flexDirection: 'row' | 'column',
    usePosition = false
) {
    const {top, left, bottom, right} = usePosition
        ? getDistanceToParentPosition(parent, child)
        : getDistanceToParentInnerPosition(parent, child);

    return flexDirection === 'row'
        ? {
              alignEdgeTopLeft: top,
              alignEdgeBottomRight: bottom,
              justifyContentLeftTop: left,
              justifyContentRightBottom: right,
          }
        : {
              alignEdgeTopLeft: left,
              alignEdgeBottomRight: right,
              justifyContentLeftTop: top,
              justifyContentRightBottom: bottom,
          };
}

export function calculateNegativeMarginChildren(parent: EnrichedAstNode, children: EnrichedAstNode[]) {
    // Could be multiple children who need negative margin treatment.
    const parentSides = getSidesOfNode(parent.position); // TODO maybe innerposition

    children.forEach((child) => {
        const childSides = getSidesOfNode(child.position);

        // left
        if (parentSides.left > childSides.left && !child.calculatedLayoutAbstraction?.userMarginSet?.marginLeft) {
            child.position.marginLeft = childSides.left - parentSides.left;
        }

        // Right
        if (parentSides.right < childSides.right && !child.calculatedLayoutAbstraction?.userMarginSet?.marginRight) {
            child.position.marginRight = parentSides.right - childSides.right;
        }

        // top
        if (parentSides.top > childSides.top && !child.calculatedLayoutAbstraction?.userMarginSet?.marginTop) {
            child.position.marginTop = childSides.top - parentSides.top;
        }

        // bottom
        if (parentSides.bottom < childSides.bottom && !child.calculatedLayoutAbstraction?.userMarginSet?.marginBottom) {
            child.position.marginBottom = parentSides.bottom - childSides.bottom;
        }

        // calculate if overlap. and not inside of parent
        // apply padding to overlapping side.
        // could actually be applied to every side D:
    });
}

function addBackEdgeGaps(start: number, end: number, gaps: Gap[]): Gap[] {
    const results = [...gaps];

    const hasStart = gaps[0]?.start === start;

    if (!hasStart) {
        results.unshift({start: start, end: start});
    }

    const hasEnd = gaps[gaps.length - 1]?.end === end;

    if (!hasEnd) {
        results.push({start: end, end: end});
    }

    return results;
}

function removeDuplicateGapValues(gaps: Gap[]) {
    const seen = {};
    return gaps.filter(function (item) {
        const k = item.start + '_' + item.end;
        return Object.prototype.hasOwnProperty.call(seen, k) ? false : (seen[k] = true);
    });
}

const gapReducer = (bounds: {start: number; end: number}) => {
    return (result, gap) => {
        const nodeStartPosition = getRelativePosition(bounds.start, gap);
        const nodeEndPosition = getRelativePosition(bounds.end, gap);

        // overlap Left
        if (nodeStartPosition === 'BEFORE' && nodeEndPosition === 'INSIDE') {
            return [
                ...result,
                {
                    start: bounds.end,
                    end: gap.end,
                },
            ];
        }
        // overlap Right
        if (nodeStartPosition === 'INSIDE' && nodeEndPosition === 'AFTER') {
            return [
                ...result,
                {
                    start: gap.start,
                    end: bounds.start,
                },
            ];
        }
        // node inside gap
        if (nodeStartPosition === 'INSIDE' && nodeEndPosition === 'INSIDE') {
            return [...result, {start: gap.start, end: bounds.start}, {start: bounds.end, end: gap.end}];
        }
        // gap inside node
        if (nodeStartPosition === 'BEFORE' && nodeEndPosition === 'AFTER') {
            return result;
        }
        // no overlap
        return [...result, gap];
    };
};

function getRelativePosition(
    point: number,
    {
        start,
        end,
    }: {
        start: number;
        end: number;
    }
) {
    if (point < start) {
        return 'BEFORE';
    }
    if (point > end) {
        return 'AFTER';
    }
    return 'INSIDE';
}

// should look for equal length groups and force those into a single group.

const MERGE_PERCENTAGE = 1.1;
// This function serves to remove smaller gaps which will create more groups.
export function adjustGrouping(gaps: Gap[]) {
    if (gaps.length < 4) {
        return gaps;
    }

    const workingGaps = [...gaps];

    workingGaps.shift();
    workingGaps.pop();

    const sizeMap: {[key: number]: number} = {};
    workingGaps.forEach((gap) => {
        const gapSize = getDistanceFromBounds(gap);
        if (sizeMap[gapSize]) {
            sizeMap[gapSize]++;
        } else {
            sizeMap[gapSize] = 1;
        }
    });

    const [biggestGap] = Object.keys(sizeMap).sort((a, b) => +b - +a);

    //    const smallestGap = rest[rest.length - 1];
    //
    // if (+biggestGap / +smallestGap < 2) {
    //     return gaps;
    // }
    //
    // if all gaps are equal return...

    const adjustedArray = workingGaps.filter((gap) => {
        return getDistanceFromBounds(gap) * MERGE_PERCENTAGE > +biggestGap;
    });

    // conditionally remove gaps
    // if large gap... remove all small gaps?
    return [gaps[0], ...adjustedArray, gaps[gaps.length - 1]];
}

export function getSides(gaps: Gaps) {
    const childLeftGap = getDistanceFromBounds(gaps.x[0]);
    const childRightGap = getDistanceFromBounds(gaps.x[gaps.x.length - 1]);

    const childTopGap = getDistanceFromBounds(gaps.y[0]);
    const childBottomGap = getDistanceFromBounds(gaps.y[gaps.y.length - 1]);

    return {top: childTopGap, bottom: childBottomGap, left: childLeftGap, right: childRightGap};
}

export function getSidesOfNode(nodePosition: AstPosition) {
    return {
        top: nodePosition.absoluteY,
        bottom: nodePosition.absoluteY + nodePosition.height,
        left: nodePosition.absoluteX,
        right: nodePosition.absoluteX + nodePosition.width,
    };
}

export function getJustifyAlignGaps(gaps: Gaps, flexDirection: 'row' | 'column') {
    const {top, bottom, left, right} = getSides(gaps);
    const [alignEdgeTopLeft, alignEdgeBottomRight] = flexDirection === 'row' ? [top, bottom] : [left, right];
    const [justifyContentLeftTop, justifyContentRightBottom] = flexDirection === 'row' ? [left, right] : [top, bottom];

    return {
        alignEdgeTopLeft,
        alignEdgeBottomRight,
        justifyContentLeftTop,
        justifyContentRightBottom,
    };
}

export function findPercentageWhiteSpace(
    node: EnrichedAstNode,
    flexChildren: EnrichedAstNode[]
): {xPercentage: number; yPercentage: number} {
    const gaps = checkGaps(node.position, flexChildren);

    const childSpaceSumX = gaps.x.reduce((acc, value) => acc + (value.end - value.start), 0);
    const childSpaceSumY = gaps.y.reduce((acc, value) => acc + (value.end - value.start), 0);

    return {xPercentage: childSpaceSumX / node.position.width, yPercentage: childSpaceSumY / node.position.height};
}
